import type { ReactNode } from 'react';
import { ArrowUpRight, Landmark } from 'lucide-react';
import { Link } from 'react-router';

import type {
  FinancingRoundSnapshot,
  FundTransactionDetail,
  ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency, formatFxRate, formatShares } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

function resolveEyebrow(snapshot: FinancingRoundSnapshot | null): string {
  const label =
    snapshot?.underlying_captable_type === 'issuance'
      ? 'Issuance'
      : 'Financing Round';
  const roundLabel = snapshot?.round_name ?? label;
  const companyName = snapshot?.portfolio_company_name;
  return companyName ? `${roundLabel} - ${companyName}` : roundLabel;
}

function renderFinancingRoundFields(
  snapshot: FinancingRoundSnapshot,
  currency: string,
  transactionAmount: number,
  companyHref: string | null
) {
  return (
    <>
      <Field label="Amount">
        {formatCurrency(transactionAmount, { currency })}
      </Field>
      <Field label="Portfolio Company">
        {companyHref && snapshot.portfolio_company_name ? (
          <Link
            to={companyHref}
            data-testid="view-financing-round-link"
            className="inline-flex items-center gap-1 text-sky-600 hover:underline dark:text-sky-400"
          >
            {snapshot.portfolio_company_name}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          (snapshot.portfolio_company_name ?? '-')
        )}
      </Field>
      {snapshot.round_name != null && (
        <Field label="Round name">{snapshot.round_name}</Field>
      )}
      {snapshot.shares_acquired != null && (
        <Field label="Shares acquired">
          {formatShares(snapshot.shares_acquired)}
        </Field>
      )}
      {snapshot.price_per_share != null && (
        <Field label="Price per share">
          {formatCurrency(Number(snapshot.price_per_share), { currency })}
        </Field>
      )}
      {snapshot.pre_money_valuation != null && (
        <Field label="Pre-money valuation">
          {formatCurrency(Number(snapshot.pre_money_valuation), { currency })}
        </Field>
      )}
      {snapshot.post_money_valuation != null && (
        <Field label="Post-money valuation">
          {formatCurrency(Number(snapshot.post_money_valuation), { currency })}
        </Field>
      )}
      {snapshot.invested_amount_base != null && (
        <Field label="Invested amount (base)">
          {formatCurrency(Number(snapshot.invested_amount_base), { currency })}
        </Field>
      )}
      {snapshot.fx_rate != null && Number(snapshot.fx_rate) !== 1 && (
        <Field label="FX rate">{formatFxRate(snapshot.fx_rate)}</Field>
      )}
    </>
  );
}

export function FinancingRoundDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
  descriptionAside,
}: {
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  descriptionAside: ReactNode;
}) {
  const snapshot = transaction.financing_round;
  const companyHref =
    snapshot?.portfolio_company_id && transaction.captable_ledger_transaction_id
      ? `/app/portfolio/${snapshot.portfolio_company_id}/transactions/${transaction.captable_ledger_transaction_id}`
      : null;

  return (
    <TransactionDetailsCard
      transaction={transaction}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      descriptionAside={descriptionAside}
      icon={<Landmark className="h-5 w-5" />}
      iconClassName="bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300"
      eyebrow={resolveEyebrow(snapshot)}
      fields={
        snapshot
          ? renderFinancingRoundFields(
              snapshot,
              transaction.currency,
              transaction.amount,
              companyHref
            )
          : null
      }
    />
  );
}
